import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Hls from 'hls.js';
import { Heart, Info, Maximize, Tv2, Loader2, ChevronDown, ChevronRight, Bookmark, BookmarkCheck, Bell, BellOff } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { catalogApi, favoritesApi, authApi, streamsApi, watchlistApi, buildWatchUrl } from '../lib/api.js';
import { useTimezone } from '../hooks/useTimezone.js';
import { useAuthStore, usePlayerStore, useUiStore } from '../lib/stores.js';
import { formatTime } from '../lib/time.js';
import styles from './Player.module.css';

export default function Player() {
  const channel = usePlayerStore((s) => s.channel);
  const token = useAuthStore((s) => s.token);

  if (!channel) return <EmptyState />;

  return (
    <section className={styles.surface}>
      {token ? (
        <ActivePlayer channel={channel} token={token} />
      ) : (
        <LockedSurface channel={channel} />
      )}
      <ChannelMeta channel={channel} token={token} />
    </section>
  );
}

function ActivePlayer({ channel, token }) {
  const videoRef = useRef(null);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState(null); // { active, limit } when 409
  const [loading, setLoading] = useState(true);
  const [stopBusy, setStopBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const watchUrl = buildWatchUrl('channels', channel.id, token);

  useEffect(() => {
    if (!videoRef.current) return;
    setError(''); setLimit(null); setLoading(true);
    const video = videoRef.current;

    let hls;
    let cancelled = false;
    const cleanup = () => {
      cancelled = true;
      if (hls) { hls.destroy(); hls = null; }
      video.removeAttribute('src');
      video.load();
    };

    // When playback fails, probe /api/me. If the session is dead, apiFetch's
    // global 401 handler clears auth and reopens the login modal.
    const onPlaybackFailure = (message) => {
      setError(message);
      authApi.me().catch(() => { /* apiFetch handles 401 */ });
    };

    // Probe the watch endpoint first. It returns 302 to the CDN on success,
    // but 401/403/409 if there's a problem we should surface in the UI
    // before hls.js gets involved. redirect:'manual' so the browser doesn't
    // fire the cross-origin redirect (which we can't read anyway).
    fetch(watchUrl, { redirect: 'manual' })
      .then(async (res) => {
        if (cancelled) return;
        // res.type === 'opaqueredirect' is the normal happy path: the
        // server told us to go to the CDN. Hand off to hls.js.
        if (res.type === 'opaqueredirect' || res.status === 0 || (res.status >= 200 && res.status < 400)) {
          startHls();
          return;
        }
        if (res.status === 409) {
          let body = null;
          try { body = await res.json(); } catch { /* ignore */ }
          setLoading(false);
          setLimit(body?.error?.details || { active: 0, limit: 0 });
          return;
        }
        if (res.status === 401) {
          // Touch /api/me so apiFetch's global handler reopens the login modal.
          onPlaybackFailure('Your session is no longer active. Please sign in again.');
          return;
        }
        if (res.status === 403) {
          onPlaybackFailure('Streaming access is not active on your account.');
          return;
        }
        onPlaybackFailure(`Could not start playback (status ${res.status}).`);
      })
      .catch(() => {
        // Probe failure usually means CORS or network down; let hls.js try
        // anyway so the user gets a real error path.
        if (!cancelled) startHls();
      });

    function startHls() {
      // Safari has native HLS — use it when available, it handles auth-cookie
      // and m3u8 redirects most gracefully.
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = watchUrl;
        video.onloadedmetadata = () => setLoading(false);
        video.onerror = () => onPlaybackFailure(
          'Playback error. The CDN may not allow CORS from this origin, or your session expired.',
        );
        video.play().catch(() => { /* user gesture might be needed */ });
        return;
      }
      if (Hls.isSupported()) {
        hls = new Hls({ enableWorker: true, lowLatencyMode: false });
        hls.loadSource(watchUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setLoading(false);
          video.play().catch(() => {});
        });
        hls.on(Hls.Events.ERROR, (_evt, data) => {
          if (!data.fatal) return;
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            const status = data.response?.code;
            if (status === 401 || status === 403) {
              onPlaybackFailure('Your session is no longer active. Please sign in again.');
            } else {
              onPlaybackFailure(
                'Network error — the CDN may not allow CORS from this origin, or your session expired.',
              );
            }
          } else {
            setError(`Playback failed: ${data.details}`);
          }
        });
        return;
      }
      setError('Your browser does not support HLS playback.');
    }

    return cleanup;
  }, [watchUrl, reloadKey]);

  const freeUpAndRetry = async () => {
    setStopBusy(true);
    try {
      // keep_current:false because the *current* session's slot is what
      // we're trying to swap into — there's no in-flight stream to spare.
      await streamsApi.stopAll({ keepCurrent: false });
      setReloadKey((n) => n + 1);
    } catch {
      setError('Could not free up the connection. Try again in a moment.');
    } finally {
      setStopBusy(false);
    }
  };

  return (
    <div className={styles.videoWrap}>
      <video
        ref={videoRef}
        className={styles.video}
        playsInline
        controls
        autoPlay
        muted={false}
      />
      {loading && !error && !limit && (
        <div className={styles.overlay}>
          <Loader2 size={28} className={styles.spin} />
          <span>Loading {channel.name}…</span>
        </div>
      )}
      {limit && (
        <div className={styles.overlay}>
          <strong>You're watching on {limit.active} of {limit.limit} devices</strong>
          <span className="muted">
            VIBE TV allows {limit.limit} concurrent streams. Stop the others to start watching here.
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn primary" onClick={freeUpAndRetry} disabled={stopBusy}>
              {stopBusy ? 'Stopping…' : 'Stop other streams & watch here'}
            </button>
          </div>
        </div>
      )}
      {error && !limit && (
        <div className={styles.overlay}>
          <strong>{channel.name}</strong>
          <span className={styles.error}>{error}</span>
          <button className="btn ghost" onClick={() => setReloadKey((n) => n + 1)}>Retry</button>
        </div>
      )}
    </div>
  );
}

function LockedSurface({ channel }) {
  const openModal = useUiStore((s) => s.openModal);
  return (
    <div className={styles.videoWrap}>
      <div className={styles.overlay}>
        <Tv2 size={32} />
        <strong>{channel.name}</strong>
        <span className="muted">Sign in to watch live</span>
        <button className="btn primary" onClick={() => openModal('login')}>Sign in</button>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <section className={styles.surface}>
      <div className={styles.videoWrap}>
        <div className={styles.overlay}>
          <Tv2 size={36} />
          <strong>Pick a channel to start watching</strong>
          <span className="muted">Channels are on the left. Sign in to unlock playback.</span>
        </div>
      </div>
    </section>
  );
}

function ChannelMeta({ channel, token }) {
  const qc = useQueryClient();
  const tz = useTimezone();
  const epgQ = useQuery({
    queryKey: ['epg', channel.slug],
    queryFn: () => catalogApi.channelEpg(channel.slug),
    refetchInterval: 5 * 60 * 1000,
    enabled: !!channel.slug,
  });
  const favoritesQ = useQuery({
    queryKey: ['favorites', 'channel'],
    queryFn: () => favoritesApi.list('channel'),
    enabled: !!token,
  });
  const favoriteId = favoritesQ.data?.results?.find((f) => f.item?.id === channel.id)?.id;
  const openModal = useUiStore((s) => s.openModal);

  const toggle = useMutation({
    mutationFn: async () => {
      if (!token) { openModal('login'); return; }
      if (favoriteId) await favoritesApi.remove(favoriteId);
      else await favoritesApi.add('channel', channel.id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['favorites', 'channel'] }),
  });

  const programs = epgQ.data?.results || [];
  // Recompute "now" only when programs change, not every render — otherwise
  // the EpgList resets its scroll position on each re-render tick.
  const nowReference = useMemo(() => new Date(), [programs.length, channel.id]);
  const current = programs.find(
    (p) => new Date(p.start_at) <= nowReference && new Date(p.end_at) > nowReference,
  );
  const next = programs.find((p) => new Date(p.start_at) > nowReference);

  const [focusCurrentTick, setFocusCurrentTick] = useState(0);
  const focusNow = useCallback(() => setFocusCurrentTick((n) => n + 1), []);

  const requestFullscreen = () => {
    const video = document.querySelector(`.${styles.video}`);
    if (video?.requestFullscreen) video.requestFullscreen();
    else if (video?.webkitEnterFullscreen) video.webkitEnterFullscreen();
  };

  return (
    <div className={styles.meta}>
      <div className={styles.metaTop}>
        <div className={styles.metaName}>
          {channel.logo_url && <img src={channel.logo_url} alt="" className={styles.metaLogo} />}
          <div>
            <h2>{channel.name}</h2>
            <div className={styles.metaSub}>
              {current ? <>Now: <strong>{current.title}</strong></> : <span className="muted">No EPG data</span>}
              {next && <span className={styles.next}> · Next: {next.title} at {formatTime(next.start_at, tz)}</span>}
            </div>
          </div>
        </div>
        <div className={styles.metaActions}>
          <button className={`btn ${favoriteId ? 'danger' : ''}`} onClick={() => toggle.mutate()} disabled={toggle.isPending}>
            <Heart size={15} fill={favoriteId ? 'currentColor' : 'none'} />
            {favoriteId ? 'Favorited' : 'Favorite'}
          </button>
          <button className="btn" onClick={focusNow} disabled={!current}>
            <Info size={15} /> Info
          </button>
          <button className="btn" onClick={requestFullscreen}><Maximize size={15} /> Full</button>
        </div>
      </div>
      {programs.length > 0 ? (
        <EpgList
          programs={programs}
          currentId={current?.id}
          focusTick={focusCurrentTick}
          tz={tz}
          token={token}
        />
      ) : (
        <div className={styles.scheduleEmpty}>
          <span className="muted">No EPG data for this channel yet.</span>
        </div>
      )}
    </div>
  );
}

function EpgList({ programs, currentId, focusTick, tz, token }) {
  const [expandedId, setExpandedId] = useState(currentId);
  const rowRefs = useRef({});
  const containerRef = useRef(null);
  const qc = useQueryClient();
  const openModal = useUiStore((s) => s.openModal);

  // Watchlist state — map epg_program_id -> { id (watchlist item), auto_switch }.
  // One fetch covers every row; the bookmark button reads from this map.
  const watchlistQ = useQuery({
    queryKey: ['watchlist'],
    queryFn: watchlistApi.list,
    enabled: !!token,
  });
  const watchlistByProgramId = useMemo(() => {
    const map = new Map();
    (watchlistQ.data || []).forEach((item) => {
      if (item.program?.id) map.set(item.program.id, item);
    });
    return map;
  }, [watchlistQ.data]);

  const addToWatchlist = useMutation({
    mutationFn: (epgProgramId) => watchlistApi.add(epgProgramId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['watchlist'] }),
  });
  const removeFromWatchlist = useMutation({
    mutationFn: (itemId) => watchlistApi.remove(itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['watchlist'] }),
  });
  const toggleAutoSwitch = useMutation({
    mutationFn: ({ itemId, autoSwitch }) => watchlistApi.update(itemId, { auto_switch: autoSwitch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['watchlist'] }),
  });

  // On channel change / first load, expand the now-playing row and scroll
  // it into view inside the list. We scroll the container, not the page,
  // so the player above stays put.
  useEffect(() => {
    if (!currentId) return;
    setExpandedId(currentId);
    const row = rowRefs.current[currentId];
    const container = containerRef.current;
    if (row && container) {
      // Center the row inside the visible area instead of using
      // scrollIntoView, which scrolls the whole document.
      const top = row.offsetTop - container.offsetTop - container.clientHeight / 2 + row.clientHeight / 2;
      container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }, [currentId, focusTick]);

  return (
    <div className={styles.schedule} ref={containerRef}>
      {programs.map((p) => {
        const isCurrent = p.id === currentId;
        const isExpanded = expandedId === p.id;
        const wlItem = watchlistByProgramId.get(p.id);
        const isSaved = !!wlItem;
        const toggleSave = (e) => {
          e.stopPropagation();
          if (!token) { openModal('login'); return; }
          if (isSaved) removeFromWatchlist.mutate(wlItem.id);
          else addToWatchlist.mutate(p.id);
        };
        return (
          <div key={p.id} ref={(el) => { rowRefs.current[p.id] = el; }} className={styles.scheduleItem}>
            <div className={`${styles.scheduleRow} ${isCurrent ? styles.scheduleRowNow : ''} ${isExpanded ? styles.scheduleRowOpen : ''}`}>
              <button
                type="button"
                className={styles.scheduleRowBody}
                onClick={() => setExpandedId(isExpanded ? null : p.id)}
                aria-expanded={isExpanded}
              >
                <span className={styles.scheduleTime}>{formatTime(p.start_at, tz)}</span>
                <span className={styles.scheduleTitle}>{p.title}</span>
                {isCurrent && <span className={styles.nowBadge}>NOW</span>}
              </button>
              <button
                type="button"
                className={`${styles.bookmarkBtn} ${isSaved ? styles.bookmarkOn : ''}`}
                onClick={toggleSave}
                aria-label={isSaved ? 'Remove from watchlist' : 'Add to watchlist'}
                disabled={addToWatchlist.isPending || removeFromWatchlist.isPending}
                title={isSaved ? 'In your watchlist' : 'Add to watchlist'}
              >
                {isSaved ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
              </button>
              <span className={styles.chev}>
                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </span>
            </div>
            {isExpanded && (
              <div className={styles.scheduleDesc}>
                <div className={styles.scheduleDescMeta}>
                  {formatTime(p.start_at, tz)} – {formatTime(p.end_at, tz)}
                  {p.category && <span> · {p.category}</span>}
                  {p.language && <span> · {p.language.toUpperCase()}</span>}
                </div>
                {p.description ? (
                  <p className={styles.scheduleDescBody}>{p.description}</p>
                ) : (
                  <p className={`${styles.scheduleDescBody} muted`}>No description for this program.</p>
                )}
                {isSaved && (
                  <button
                    type="button"
                    className={styles.autoSwitchBtn}
                    onClick={() => toggleAutoSwitch.mutate({ itemId: wlItem.id, autoSwitch: !wlItem.auto_switch })}
                    disabled={toggleAutoSwitch.isPending}
                  >
                    {wlItem.auto_switch
                      ? <><Bell size={13} /> Auto-switch on · click to disable</>
                      : <><BellOff size={13} /> Saved · enable auto-switch</>}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

